import React, { useState, useEffect } from 'react';
import {
  Button, IconButton, Typography, Box,
  FormControl, InputLabel, OutlinedInput, Tooltip,
  Autocomplete, TextField,
} from "@mui/material";
import { useSnackbar } from 'notistack';
import { ACTIONS, MESSAGES } from '../../../shared/constants';
import DataTable from '../../../shared/data-table';
import {
  getEmployees,
  getAllAirports,
  getAllCompanies,
  getCompaniesByEmployee,
  UpdateAllCompaniesByEmployee,
  deleteCompanies,
  bulkAssignCompanies,
} from './logic';
import ManagementCompanyDetail from '../../gestion/management-company/management-companyByEmployee-Source';
import ManagementCompanyBulkAssign from '../../gestion/management-company/management-companyBulkAssign';
import DeleteIcon from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import AddCircle from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorSnackbar from '../../error-snackbar';
import BackDrop from '../../../shared/backdropDE';
import { getUser } from '../../../shared/auth-service';
import AlertDialogQuestion from '../../alert-dialog-question';

const ManagementCompany = () => {
  const [data, setData] = useState(null);
  const [showModal, setShowModal] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [action, setAction] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [optionDetails, setOptionDetails] = useState(null);
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [airports, setAirports] = useState([]);
  const [selectedAirport, setSelectedAirport] = useState(0);
  const [alertQuestionOpen, setAlertQuestionOpen] = useState(false);
  const [idDelete, setIdDelete] = useState(null);
  const companyId = user.countryCode.split('-')[0];
  const [identificationFilter, setIdentificationFilter] = useState('');
  const [dataFilter, setDataFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState(null);
  const [airportFilter, setAirportFilter] = useState(null);
  const searchInputRef = React.useRef(null);

  useEffect(() => {
    async function fetchData() {
      if (loading) {
        try {
          const data = await getEmployees(companyId);
          const companiesData = await getAllCompanies();
          const airportsData = await getAllAirports(companyId);
          setSelectedAirport(user.airport.id);
          setAirports(airportsData);
          setCompanies(companiesData);
          setLoading(false);
          setData(data);
        } catch (error) {
          setError(error.message);
          setLoading(false);
          setData([]);
        }
      }
    }
    fetchData();
  });

  const handleFilterEmployees = (value) => {
    setIdentificationFilter(value);
  };

  useEffect(() => {
    if (!data) return;
    let result = data;

    if (identificationFilter) {
      const search = identificationFilter.toUpperCase();
      result = result.filter(item =>
        item.employee.employeeId.toString().toUpperCase().includes(search) ||
        `${item.employee.name} ${item.employee.lastName}`.toUpperCase().includes(search)
      );
    }

    if (companyFilter) {
      result = result.filter(item =>
        item.companies?.some(c => c.id === companyFilter.id) ?? false
      );
    }

    if (airportFilter) {
      result = result.filter(item =>
        item.employee.airport.code === airportFilter.code
      );
    }

    setDataFilter(result);
  }, [identificationFilter, companyFilter, airportFilter, data]);

  const handleDeleteData = (cellValues) => {
    setAlertQuestionOpen(true);
    setIdDelete(cellValues.row.employee.employeeId);
  };

  const handleCloseAlert = () => {
    setAlertQuestionOpen(false);
    setIdDelete(null);
  };

  const clearFilters = () => {
    setIdentificationFilter('');
    setDataFilter([]);
    setCompanyFilter(null);
    setAirportFilter(null);
  };

  const handleSave = async (companies) => {
    await UpdateAllCompaniesByEmployee(companies, user.employeeId);
    setShowModal(false);
    setLoading(true);
    clearFilters();
    enqueueSnackbar(MESSAGES.SAVED);
  };

  const handleBulkSave = async ({ employeeIds, companies: companiesList }) => {
    await bulkAssignCompanies(employeeIds, companiesList, user.employeeId);
    setShowBulkModal(false);
    setLoading(true);
    clearFilters();
    enqueueSnackbar(MESSAGES.SAVED);
  };

  const handleDelete = async () => {
    await deleteCompanies(idDelete, user.employeeId);
    setShowModal(false);
    setAlertQuestionOpen(false);
    setLoading(true);
    clearFilters();
    enqueueSnackbar(MESSAGES.DELETE);
  };

  const handleEdit = (e, cellValues) => {
    e.stopPropagation();
    setSelectedAirport(cellValues.row.employee.airport.id);
    setEmployeeId(cellValues.row.employee.employeeId);
    fetchOptionDetails(cellValues.row.employee.employeeId);
  };

  const fetchOptionDetails = async (employeeId) => {
    try {
      const details = await getCompaniesByEmployee(employeeId);
      setOptionDetails(details);
      setShowModal(true);
      setAction(ACTIONS.Edit);
    } catch (error) {
      setError(error.message);
    }
  };

  const columns = [
    { field: 'id', headerName: 'Código', flex: 0.2, hide: true },
    {
      field: 'employeeId', headerName: 'Identificación', flex: 0.3,
      valueGetter: (params) => `${params.row.employee.employeeId || ''}`,
    },
    {
      field: 'name', headerName: 'Nombre Completo', flex: 1,
      valueGetter: (params) => `${params.row.employee.name || ''} ${params.row.employee.lastName || ''}`,
    },
    {
      field: 'homeAirport', headerName: 'Aeropuerto', flex: 0.3,
      align: 'center', headerAlign: 'center',
      valueGetter: (params) => `${params.row.employee.airport?.code || ''}`,
    },
    {
      field: 'cargo', headerName: 'Cargo', flex: 0.8,
      valueGetter: (params) => `${params.row.employee.employeePosition?.name || ''}`,
    },
    {
      field: 'companyId', headerName: 'Compañías Asignadas', flex: 0.3,
      align: 'center', headerAlign: 'center',
      valueGetter: (params) => params.row.companies.length || 0,
    },
    {
      field: "Editar", headerName: "Editar", width: 60, sortable: false,
      renderCell: (cellValues) => (
        <IconButton color="info" onClick={(e) => handleEdit(e, cellValues)}><Edit /></IconButton>
      )
    },
    {
      field: "Borrar", headerName: "Borrar", width: 60, sortable: false,
      renderCell: (cellValues) => (
        <IconButton color="error" onClick={() => handleDeleteData(cellValues)}><DeleteIcon /></IconButton>
      )
    },
  ];

  if (loading) return <BackDrop />;
  if (error) return <ErrorSnackbar message={error} />;

  return (
    <>
      {showBulkModal && (
        <ManagementCompanyBulkAssign
          airports={airports}
          companies={companies}
          handleClose={() => setShowBulkModal(false)}
          handleSave={handleBulkSave}
        />
      )}

      {showModal && (
        <ManagementCompanyDetail
          data={optionDetails}
          employeeId={employeeId}
          airportsSelected={companies}
          handleClose={() => setShowModal(false)}
          handleSave={handleSave}
          airportId={selectedAirport}
          airports={airports}
          action={action}
        />
      )}

      {alertQuestionOpen && (
        <AlertDialogQuestion
          handlePositive={handleDelete}
          handleNegative={handleCloseAlert}
          textButtonPositive={ACTIONS.ACCEPT}
          textButtonNegative={ACTIONS.CANCEL}
          dialogContentText={MESSAGES.CONFIRM_DELETE}
          dialogTitle=""
          open={alertQuestionOpen}
          elemento=""
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1, px: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
          <Typography variant="h5" style={{ color: '#1a3072' }} fontWeight="bold">
            Compañías Adicionales Por <span style={{ color: '#00b0ef' }}>Empleado</span>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Tooltip title="Filtrar por número de documento o nombre completo">
            <FormControl sx={{ width: 240 }} variant="outlined" size="small">
              <InputLabel htmlFor="search-company-employee">Buscar empleado</InputLabel>
              <OutlinedInput
                id="search-company-employee"
                inputRef={searchInputRef}
                type="text"
                value={identificationFilter}
                onChange={(e) => { setIdentificationFilter(e.target.value); handleFilterEmployees(e.target.value); }}
                endAdornment={
                  identificationFilter
                    ? <IconButton size="small" onClick={() => setIdentificationFilter('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    : <SearchIcon sx={{ color: '#aaa' }} />
                }
                label="Buscar empleado"
              />
            </FormControl>
          </Tooltip>
          <Autocomplete
            size="small"
            sx={{ width: 180 }}
            options={airports}
            getOptionLabel={(o) => o.code || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={airportFilter}
            onChange={(_, newValue) => setAirportFilter(newValue)}
            renderInput={(params) => <TextField {...params} label="Aeropuerto" variant="outlined" />}
            noOptionsText="Sin resultados"
          />
          <Autocomplete
            size="small"
            sx={{ width: 220 }}
            options={companies}
            getOptionLabel={(o) => o.shortName || o.name || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={companyFilter}
            onChange={(_, newValue) => setCompanyFilter(newValue)}
            renderInput={(params) => <TextField {...params} label="Compañía" variant="outlined" />}
            noOptionsText="Sin resultados"
          />
          <Button endIcon={<AddCircle />} variant="contained" onClick={() => setShowBulkModal(true)}>
            {ACTIONS.NEW}
          </Button>
        </Box>
      </Box>

      <DataTable
        columns={columns}
        rows={!identificationFilter && !companyFilter && !airportFilter ? data : dataFilter}
        sx={{ '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' } }}
      />
    </>
  );
};

export default ManagementCompany;
